import { useEffect, useState } from 'react';
import { Toasts } from './components/Toasts.tsx';
import { TooltipHost } from './components/TooltipHost.tsx';
import { EditorLayout } from './features/EditorLayout.tsx';
import { ProjectPicker } from './features/project/ProjectPicker.tsx';
import { useEditor } from './state/editor-store.ts';
import { closeProject, openRecentProject, saveNow } from './state/session.ts';
import { projectStore } from './projects/setup.ts';
import { mediaSupport } from './media/setup.ts';
import { UnsupportedBrowser } from './features/project/UnsupportedBrowser.tsx';
import { errorMessage, toast } from './state/toast-store.ts';

function projectIdFromHash(): string | null {
  return /^#\/project\/([\w-]+)/.exec(window.location.hash)?.[1] ?? null;
}

/** Project currently being opened (guards against duplicate opens, e.g. StrictMode effects). */
let opening: string | null = null;

export function App() {
  const project = useEditor((s) => s.project);
  const [booting, setBooting] = useState(() => projectIdFromHash() !== null);

  // The URL hash is the source of truth for which project is open (deep links, back/forward).
  useEffect(() => {
    const sync = async () => {
      const id = projectIdFromHash();
      if (id === (useEditor.getState().project?.id ?? null) || (id !== null && id === opening)) return;
      await saveNow();
      if (!id) {
        closeProject();
        return;
      }
      // Reopen automatically only when the browser still grants folder access; otherwise the
      // start screen offers a one-click reopen (permission prompts need a user gesture).
      if ((await projectStore.access(id)) !== 'granted') {
        closeProject();
        setBooting(false);
        return;
      }
      setBooting(true);
      opening = id;
      try {
        await openRecentProject(id);
      } catch (err) {
        toast.error(`Could not open project: ${errorMessage(err)}`);
        window.location.hash = '';
      } finally {
        opening = null;
        setBooting(false);
      }
    };
    void sync();
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, []);

  const support = mediaSupport();
  if (!support.supported) return <UnsupportedBrowser reason={support.reason} />;

  return (
    <>
      {project ? (
        <EditorLayout />
      ) : booting ? (
        <div className="boot">Loading project…</div>
      ) : (
        <ProjectPicker highlightId={projectIdFromHash()} />
      )}
      <Toasts />
      <TooltipHost />
    </>
  );
}
